import React from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import moment from "moment";
import { INK, SERIES } from "./chartTheme";

/** A sprint wears the same dot colour here that its state wears on the page. */
const stateDot = (sprint) =>
  sprint?.active ? "#0f9d6a" : sprint?.completed ? SERIES[0] : INK.muted;

const stateLabel = (sprint) =>
  sprint?.active ? "Active" : sprint?.completed ? "Completed" : "Not started";

const rangeOf = (sprint) => {
  const start = sprint?.startDate ? moment(sprint.startDate) : null;
  const end = sprint?.endDate ? moment(sprint.endDate) : null;
  return start && end ? `${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}` : "";
};

/**
 * The one control every panel on the page reads, so it sits beside the project
 * title rather than off in a corner. It is built as an icon + two lines to
 * mirror the project block next to it — the two read as a matched pair.
 */
export default function SprintPicker({ options = [], value = "", onChange = () => {} }) {
  const selected = options.find((sprint) => sprint?._id === value);
  const range = rangeOf(selected);

  return (
    <Select
      value={options.length ? value || "" : ""}
      onChange={onChange}
      displayEmpty
      IconComponent={ExpandMoreRoundedIcon}
      renderValue={() => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, width: "100%" }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#eef3fc",
              color: SERIES[0],
            }}
          >
            <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
          </Box>

          <Box sx={{ minWidth: 0, textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.875, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14.5px !important",
                  fontWeight: "650 !important",
                  color: selected ? INK.primary : INK.muted,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selected?.name || "Select sprint"}
              </Typography>
              {selected ? (
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "999px",
                    flex: "0 0 auto",
                    backgroundColor: stateDot(selected),
                    boxShadow: selected?.active ? "0 0 0 3px rgba(15,157,106,0.16)" : "none",
                  }}
                />
              ) : null}
            </Box>
            <Typography
              sx={{
                fontSize: "11.5px !important",
                color: INK.muted,
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selected ? range || stateLabel(selected) : "Filters every panel below"}
            </Typography>
          </Box>
        </Box>
      )}
      sx={{
        width: { xs: "100%", sm: 300 },
        height: 58,
        borderRadius: "14px",
        backgroundColor: "#fbfcfe",
        transition: "background-color .2s ease, box-shadow .2s ease",
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          height: "100% !important",
          boxSizing: "border-box",
          py: 0,
          pl: 1.25,
          pr: "40px !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: INK.border,
          borderWidth: "1px !important",
        },
        "&:hover": { backgroundColor: "#f6f8fc" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd6e8" },
        "&.Mui-focused": { backgroundColor: INK.surface, boxShadow: "0 0 0 4px rgba(42,120,214,0.12)" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: SERIES[0] },
        "& .MuiSelect-icon": {
          color: INK.muted,
          right: 12,
          fontSize: 22,
          transition: "transform .2s ease",
        },
      }}
      MenuProps={{
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        PaperProps: {
          sx: {
            mt: 1,
            minWidth: 300,
            borderRadius: "14px",
            border: `1px solid ${INK.border}`,
            boxShadow:
              "0 4px 10px rgba(17,24,39,0.06), 0 22px 44px -24px rgba(17,24,39,0.35)",
            maxHeight: 360,
            "& .MuiList-root": { py: 0.75 },
          },
        },
      }}
    >
      {options.map((sprint) => {
        const isSelected = sprint?._id === value;
        return (
          <MenuItem
            key={sprint?._id}
            value={sprint?._id}
            sx={{
              mx: 0.75,
              my: 0.25,
              px: 1.25,
              py: 1,
              borderRadius: "10px",
              alignItems: "center",
              gap: 1.25,
              "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "#eef4fd" },
              "&:hover": { backgroundColor: "#f4f6fb" },
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "999px",
                flex: "0 0 auto",
                backgroundColor: stateDot(sprint),
              }}
            />
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography
                sx={{
                  fontSize: "14px !important",
                  fontWeight: isSelected ? "650 !important" : "500 !important",
                  color: INK.primary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {sprint?.name}
              </Typography>
              {/* state is spelled out, never carried by the dot's colour alone */}
              <Typography sx={{ fontSize: "11.5px !important", color: INK.muted, mt: "1px" }}>
                {[rangeOf(sprint), stateLabel(sprint)].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            {isSelected ? (
              <CheckRoundedIcon sx={{ fontSize: 18, color: SERIES[0], flex: "0 0 auto" }} />
            ) : null}
          </MenuItem>
        );
      })}
    </Select>
  );
}
